// src/components/DetailsEditor.js - Updated with ProfileCropper and ESLint fixes
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, Mail, Phone, FileText, Palette, Droplets, Image, Camera } from 'lucide-react';
import BannerUpload from './BannerUpload';
import ProfileCropper from './ProfileCropper'; // Import the new ProfileCropper
import { storage } from '../firebase'; // Removed unused imports (db, doc, updateDoc)
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import styles from './DetailsEditor.module.css';

const DetailsEditor = ({ initialDetails, onSave, onColorChange, userId, userData, user }) => {
  // Simple state without complicated derivations
  const [displayName, setDisplayName] = useState(initialDetails.displayName || '');
  const [email, setEmail] = useState(initialDetails.email || '');
  const [phone, setPhone] = useState(initialDetails.phone || '');
  const [bio, setBio] = useState(initialDetails.bio || '');
  const [textColor, setTextColor] = useState(initialDetails.textColor?.substring(0, 7) || '#000000');
  const [textOpacity, setTextOpacity] = useState(initialDetails.textOpacity || 100);
  const [bannerData, setBannerData] = useState(initialDetails.banner || null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  // State variables
  const [bannerError, setBannerError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  // New state for checking if the color is light
  const [isLightColor, setIsLightColor] = useState(false);
  
  // New state for profile cropper
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  
  // Refs for the color inputs and file input
  const colorPickerRef = useRef(null);
  const colorTextRef = useRef(null);
  const fileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null); // New ref for banner file input
  
  // Store previous values to avoid unnecessary updates
  const prevInitialDetails = useRef(initialDetails);

  // Initialize from props - but only if not actively editing
  useEffect(() => {
    // Skip this update if user is actively editing or values haven't actually changed
    if (isEditing) {
      console.log('Skipping initialDetails update - user is actively editing');
      return;
    }
    
    // Check if actual values have changed to prevent unnecessary updates
    const prevDetails = prevInitialDetails.current;
    const hasChanged = 
      prevDetails.displayName !== initialDetails.displayName ||
      prevDetails.email !== initialDetails.email ||
      prevDetails.phone !== initialDetails.phone ||
      prevDetails.bio !== initialDetails.bio ||
      prevDetails.textColor !== initialDetails.textColor ||
      prevDetails.textOpacity !== initialDetails.textOpacity ||
      JSON.stringify(prevDetails.banner) !== JSON.stringify(initialDetails.banner);
      
    if (!hasChanged) {
      console.log('Skipping initialDetails update - values have not changed');
      return;
    }
    
    // Store new values as previous for next comparison
    prevInitialDetails.current = {...initialDetails};
    
    console.log('Updating from initialDetails, color:', initialDetails.textColor);
    console.log('Updating from initialDetails, banner:', initialDetails.banner);
    
    setDisplayName(initialDetails.displayName || '');
    setEmail(initialDetails.email || '');
    setPhone(initialDetails.phone || '');
    setBio(initialDetails.bio || '');
    setBannerData(initialDetails.banner || null);
    
    // Handle color with opacity
    let color = initialDetails.textColor || '#444444';
    let opacity = initialDetails.textOpacity || 70;
    
    // If color includes opacity (8 characters), extract base color
    if (color && color.length === 9) {
      color = color.substring(0, 7);
    }
    
    console.log('Setting color to:', color, 'and opacity to:', opacity);
    
    setTextColor(color);
    setTextOpacity(opacity);
    
    // Check if the color is light
    checkIfColorIsLight(color);
    
    // Directly update the DOM inputs for immediate visual feedback
    if (colorPickerRef.current) {
      colorPickerRef.current.value = color;
    }
    if (colorTextRef.current) {
      colorTextRef.current.value = color;
    }
  }, [initialDetails, isEditing]);

  // Function to check if a color is light
  const checkIfColorIsLight = (hexColor) => {
    // Remove the # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    // Calculate perceived brightness (human eye favors green)
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114);
    
    // If brightness is high (close to white), set isLightColor to true
    setIsLightColor(brightness > 0.75);
  };

  // Function to notify parent of color changes for live preview
  const updatePreview = useCallback(() => {
    if (onColorChange) {
      // Calculate the color with opacity
      const hexOpacity = Math.round((textOpacity / 100) * 255)
        .toString(16)
        .padStart(2, '0');
      const fullColor = `${textColor}${hexOpacity}`;
      
      console.log('Updating preview with:', fullColor, textOpacity);
      
      // Call the parent's callback
      onColorChange(fullColor, textOpacity);
    }
  }, [onColorChange, textColor, textOpacity]);

  // Call updatePreview when color or opacity changes
  useEffect(() => {
    console.log('Color or opacity changed - updating preview');
    updatePreview();
  }, [textColor, textOpacity, updatePreview]);

  // Calculate the current color with opacity
  const getColorWithOpacity = () => {
    // For display purposes, ensure text remains readable
    const displayOpacity = Math.max(0.7, textOpacity / 100);
    return `${textColor}${Math.round(displayOpacity * 255).toString(16).padStart(2, '0')}`;
  };

  // Handle color input change from the color picker
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    console.log('Color picker changed to:', newColor);
    
    // Set editing mode to prevent initialDetails override
    setIsEditing(true);
    setTextColor(newColor);
    
    // Check if the new color is light
    checkIfColorIsLight(newColor);
    
    // Force update the text input
    if (colorTextRef.current) {
      colorTextRef.current.value = newColor;
    }
    
    // Clear editing mode after a delay
    setTimeout(() => setIsEditing(false), 2000);
  };

  // Handle text input for color changes
  const handleColorTextChange = (e) => {
    let newColor = e.target.value;
    console.log('Color text changed to:', newColor);
    
    // Set editing mode to prevent initialDetails override
    setIsEditing(true);
    
    // Add # if missing
    if (newColor.charAt(0) !== '#' && /^[0-9A-Fa-f]{3,6}$/.test(newColor)) {
      newColor = `#${newColor}`;
    }
    
    // Validate if it's a proper hex color
    if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(newColor)) {
      setTextColor(newColor);
      
      // Check if the new color is light
      checkIfColorIsLight(newColor);
      
      // Force update the color picker
      if (colorPickerRef.current) {
        colorPickerRef.current.value = newColor;
      }
    }
    
    // Clear editing mode after a delay
    setTimeout(() => setIsEditing(false), 2000);
  };

  // Handle opacity slider change
  const handleOpacityChange = (e) => {
    const newOpacity = parseInt(e.target.value, 10);
    console.log('Opacity changed to:', newOpacity);
    
    // Set editing mode to prevent initialDetails override
    setIsEditing(true);
    setTextOpacity(newOpacity);
    
    // Clear editing mode after a delay
    setTimeout(() => setIsEditing(false), 2000);
  };

  // Handle banner upload WITHOUT autosave
  const handleBannerUpload = async (newBannerData) => {
    console.log('handleBannerUpload called with:', newBannerData);
    
    // Reset any previous errors
    setBannerError('');
    
    // Set editing mode to prevent initialDetails override
    setIsEditing(true);
    
    // Update the banner data in local state only - no saving to database
    setBannerData(newBannerData);
    
    // Clear editing mode after a short delay
    setTimeout(() => setIsEditing(false), 2000);
  };

  // NEW FUNCTION: Handle manual banner file selection
  const handleBannerFileSelection = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Clear the input value right after getting the file
      e.target.value = '';
      
      // Check file size
      const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
      if (file.size > MAX_FILE_SIZE) {
        setBannerError(`File too large (max 5MB). This file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
        return;
      }
      
      // Process the file locally without triggering any auto-save
      try {
        const fileUrl = URL.createObjectURL(file);
        
        // Create new banner data
        const newBannerData = {
          url: fileUrl,
          position: 50, // Default position
          fileName: file.name,
          file: file, // Store the file for later upload
          storagePath: null,
          isDefault: false,
          _manualChange: true // Flag to indicate this was changed manually
        };
        
        // Only update local state - don't call any callbacks
        setBannerData(newBannerData);
        setBannerError('');
        
        console.log('Banner file manually selected - changes will be saved when you click Save Changes');
      } catch (error) {
        console.error('Error processing banner file:', error);
        setBannerError(`Error processing file: ${error.message}`);
      }
    }
  };

  // UPDATED: Handle profile picture upload to show cropper instead of direct upload
  const handleProfilePicture = (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    
    // Clear the input value
    e.target.value = '';
    
    // Validate file type and size before proceeding
    if (!validateFileUpload(file)) {
      return;
    }
    
    // Create object URL for the cropper
    const imageUrl = URL.createObjectURL(file);
    setCropperImage(imageUrl);
    setShowCropper(true);
  };
  
  // Handle when cropper completes
  const handleCropComplete = (photoURL) => {
    // Update UI with new photo
    if (userData) {
      userData.photoURL = photoURL;
    }
    
    // Clean up and close cropper
    setShowCropper(false);
    setCropperImage(null);
  };
  
  // Handle cropper cancel
  const handleCropCancel = () => {
    setShowCropper(false);
    setCropperImage(null);
    
    // Clean up the object URL
    if (cropperImage) {
      URL.revokeObjectURL(cropperImage);
    }
  };
  
  // Validate file upload
  const validateFileUpload = (file) => {
    // Check file type
    const validFileTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validFileTypes.includes(file.type)) {
      alert('Please upload a valid image file (JPEG, PNG, GIF, or WEBP)');
      return false;
    }
    
    // Check file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      alert('Image file size must be less than 5MB');
      return false;
    }
    
    return true;
  };

  // Handle form submission - FIXED VERSION
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      // Set editing mode to prevent initialDetails override during save
      setIsEditing(true);
      
      // Create the full color with opacity for saving
      const hexOpacity = Math.round((textOpacity / 100) * 255)
        .toString(16)
        .padStart(2, '0');
      const fullColor = `${textColor}${hexOpacity}`;
      
      console.log('Saving with color:', fullColor, 'and opacity:', textOpacity);
      console.log('Saving with banner:', bannerData);
      
      // Clean any temporary flags from banner data and upload file if needed
      let cleanedBannerData = bannerData;
      if (bannerData) {
        // Check if there's a file that needs to be uploaded first
        // Handle both _manualChange flag and blob URLs
        if (bannerData.file || bannerData.url?.startsWith('blob:')) {
          try {
            console.log('Uploading banner file to storage before saving...');
            
            // Get the file either from bannerData.file or create from blob URL if needed
            let fileToUpload = bannerData.file;
            
            // If we don't have a file but have a blob URL, we need to fetch it first
            if (!fileToUpload && bannerData.url?.startsWith('blob:')) {
              console.log('Converting blob URL to file...');
              try {
                const response = await fetch(bannerData.url);
                const blob = await response.blob();
                const fileName = bannerData.fileName || 'banner.jpg';
                fileToUpload = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
                console.log('Successfully converted blob to file');
              } catch (blobError) {
                console.error('Error converting blob URL to file:', blobError);
                throw new Error('Failed to process the banner image from blob URL');
              }
            }
            
            if (!fileToUpload) {
              throw new Error('No valid file to upload for banner');
            }
            
            // Create a unique file path
            const fileExtension = fileToUpload.name.split('.').pop() || 'jpg';
            const fileName = `banners/${user.uid}_${Date.now()}.${fileExtension}`;
            const storageRef = ref(storage, fileName);
            
            // Upload the file
            const uploadResult = await uploadBytes(storageRef, fileToUpload);
            
            // Get download URL - this is a permanent Firebase Storage URL
            const downloadURL = await getDownloadURL(uploadResult.ref);
            
            // Create new banner data with storage path and permanent URL
            cleanedBannerData = {
              url: downloadURL, // This is the permanent Firebase Storage URL
              position: bannerData.position || 50,
              storagePath: fileName, // The path in Firebase Storage
              isDefault: false
            };
            
            console.log('Banner image uploaded successfully:', cleanedBannerData);
          } catch (uploadError) {
            console.error('Error uploading banner image:', uploadError);
            alert('Error uploading banner image: ' + uploadError.message);
            setIsSaving(false);
            setIsEditing(false);
            return;
          }
        } else {
          // Just remove temporary flags without changing the banner data otherwise
          const { preventEditingStateChange, disableAutoSave, _manualChange, file, ...cleanData } = bannerData;
          cleanedBannerData = cleanData;
        }
      }
      
      // Create complete details object
      const updatedDetails = {
        displayName,
        email,
        phone,
        bio,
        textColor: fullColor,
        textOpacity,
        banner: cleanedBannerData // Use cleaned banner data
      };
      
      // Save changes to Firestore
      await onSave(updatedDetails);
      
      // Update previous values reference and state
      prevInitialDetails.current = {
        ...updatedDetails,
        textColor: fullColor,
        banner: cleanedBannerData
      };
      
      // Update the local banner state to match what we saved
      setBannerData(cleanedBannerData);
      
      // Show success message
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving details:', error);
      alert('Failed to save profile details: ' + error.message);
    } finally {
      setIsSaving(false);
      
      // Clear editing mode after a delay
      setTimeout(() => setIsEditing(false), 500);
    }
  };

  // Current color with opacity for display
  const currentColorWithOpacity = getColorWithOpacity();
  
  // Input style with the selected color and background based on color brightness
  const inputStyle = {
    color: currentColorWithOpacity,
    background: isLightColor ? '#a0a0a0' : 'white',
  };

  return (
    <div className={styles.detailsContainer}>
      {/* Profile Photo Upload Section */}
      <div className={styles.photoUploadSection}>
        <div 
          className={styles.profilePhoto}
          onClick={() => fileInputRef.current.click()}
        >
          {userData?.photoURL ? (
            <img 
              src={userData.photoURL} 
              alt="Profile" 
              className={styles.profileImage}
              onError={(e) => {
                e.target.src = '';
              }}
            />
          ) : (
            <div className={styles.profileInitials}>
              {userData?.profile?.displayName
                ? userData.profile.displayName.charAt(0).toUpperCase()
                : (userData?.username ? userData.username.charAt(0).toUpperCase() : 'U')
              }
            </div>
          )}
          <div className={styles.photoOverlay}>
            <Camera size={24} />
            <span>Change Photo</span>
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleProfilePicture}
            style={{ display: 'none' }}
          />
        </div>
        <p className={styles.photoHint}>Click to upload a new profile picture</p>
      </div>

      {/* Profile Cropper (only shown when active) */}
      {showCropper && cropperImage && (
        <ProfileCropper
          image={cropperImage}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
          userId={user.uid}
        />
      )}

      <form onSubmit={handleSubmit} className={styles.detailsForm}>
        {/* Banner Upload Section */}
        <div className={styles.inputGroup}>
          <label>
            <Image size={18} />
            <span>Profile Banner</span>
          </label>
          {bannerError && <div className={styles.errorMessage}>{bannerError}</div>}
          <div className={styles.bannerWrapper}>
            <BannerUpload 
              currentBanner={bannerData}
              onBannerUpload={handleBannerUpload}
              editable={true}
              userId={user?.uid}
              useDefaultBanner={true}
            />
          </div>
          
          {/* Hidden file input */}
          <input
            type="file"
            ref={bannerFileInputRef}
            accept="image/*"
            onChange={handleBannerFileSelection}
            style={{ display: 'none' }}
          />
          
          <p className={styles.photoHint}>
            {bannerData?._manualChange
              ? "Banner changed. Click Save Changes below to save."
              : bannerData?.isDefault 
              ? "Using default banner. Click button to change banner and then Save Changes." 
              : "Click button to change banner. Remember to save changes."}
          </p>
        </div>

        {/* Buttons side-by-side container */}
        <div className={styles.buttonsContainer}>
          {/* Banner Change Button */}
          <button 
            type="button"
            className={styles.bannerChangeButton}
            onClick={() => bannerFileInputRef.current && bannerFileInputRef.current.click()}
          >
            <Camera size={16} />
            <span>Change Banner</span>
          </button>
          
          {/* Save Changes Button */}
          <button 
            type="submit" 
            className={styles.saveButton}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        
        {/* Success message */}
        {saveSuccess && <div className={styles.saveSuccess}>Details saved successfully!</div>}

        <div className={styles.inputGroup}>
          <label>
            <User size={18} />
            <span>Display Name</span>
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => {
              setIsEditing(true);
              setDisplayName(e.target.value);
              setTimeout(() => setIsEditing(false), 2000);
            }}
            placeholder="Your display name"
            style={inputStyle}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>
            <Mail size={18} />
            <span>Email</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => {
              setIsEditing(true);
              setEmail(e.target.value);
              setTimeout(() => setIsEditing(false), 2000);
            }}
            placeholder="Your email address"
            style={inputStyle}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>
            <Phone size={18} />
            <span>Phone</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => {
              setIsEditing(true);
              setPhone(e.target.value);
              setTimeout(() => setIsEditing(false), 2000);
            }}
            placeholder="Your phone number"
            style={inputStyle}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>
            <FileText size={18} />
            <span>Bio</span>
          </label>
          <textarea
            value={bio}
            onChange={(e) => {
              setIsEditing(true);
              setBio(e.target.value);
              setTimeout(() => setIsEditing(false), 2000);
            }}
            placeholder="Tell us about yourself"
            rows={4}
            style={inputStyle}
          />
        </div>

        <div className={styles.inputGroup}>
          <label>
            <Palette size={18} />
            <span>Text Color</span>
          </label>
          <div className={styles.colorInputWrapper}>
            <input
              ref={colorPickerRef}
              type="color"
              value={textColor}
              onChange={handleColorChange}
              className={styles.colorInput}
              onMouseDown={() => setIsEditing(true)}
            />
            <input
              ref={colorTextRef}
              type="text"
              value={textColor}
              onChange={handleColorTextChange}
              onFocus={() => setIsEditing(true)}
              className={styles.colorText}
              placeholder="#444444"
              style={inputStyle}
            />
            <div 
  className={styles.colorPreview} 
  style={{ 
    backgroundColor: textColor,
    border: '2px solid #d0d0d0',
    boxShadow: '0 0 0 2px #333333'
  }}
></div>
          </div>
        </div>

        <div className={styles.inputGroup}>
          <label>
            <Droplets size={18} />
            <span>Text Opacity: {textOpacity}%</span>
          </label>
          <div className={styles.opacitySliderContainer}>
            <input
              type="range"
              min="0"
              max="100"
              value={textOpacity}
              onChange={handleOpacityChange}
              className={styles.opacitySlider}
              onMouseDown={() => setIsEditing(true)}
            />
            <div 
              className={styles.opacityPreview}
              style={{ 
                background: `linear-gradient(to right, transparent, ${textColor})`,
                opacity: textOpacity / 100
              }}
            ></div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DetailsEditor;